import { ReactNode, useState } from 'react'
import { Box, Flex, Text } from '@radix-ui/themes'
import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'
import ClockIcon from '@/assets/svg/clock-icon'
import StarIcon from '@/assets/svg/star-icon'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const QuestionItemContainer = ({
  children,
  score,
  time,
  onClick,
  isEditing,
  editableScore,
  editableTime,
}: {
  children: ReactNode
  score: string | number
  time: string | number
  onClick?: () => void
  isEditing?: boolean
  editableScore?: {
    value: number
    onChange: (value: number) => void
    invalid?: boolean
  }
  editableTime?: {
    value: number
    onChange: (value: number) => void
    invalid?: boolean
  }
}) => {
  return (
    <Flex
      direction={'column'}
      className='w-full cursor-pointer rounded-[12px] border bg-white px-6 py-5 transition-all duration-300 ease-in-out hover:border-[1px] hover:border-[#0069E0] hover:shadow-[0_3px_3px_#023A7B0A]'
      onClick={onClick}
    >
      {children}
      <div className='my-2 border-b-[1px] border-[#e6eef6]'></div>
      {/* time  */}
      <Flex justify={'end'} gap={'32px'}>
        <Flex align={'center'} gap={'8px'} className='text-sm text-[#1D2939]'>
          <StarIcon />
          {isEditing && editableScore ? (
            <div className='flex items-center gap-2'>
              <span>Marks Available:</span>
              <div className='w-24'>
                <Input
                  type='number'
                  min={1}
                  value={
                    Number.isNaN(editableScore.value) ? '' : editableScore.value
                  }
                  onChange={(e) =>
                    editableScore.onChange(Number(e.target.value))
                  }
                  className={
                    editableScore.invalid
                      ? 'border-red-500 focus:border-red-500'
                      : ''
                  }
                  aria-invalid={editableScore.invalid || undefined}
                />
                {editableScore.invalid && (
                  <p className='mt-1 text-xs text-red-500'>
                    Score must be at least 1
                  </p>
                )}
              </div>
            </div>
          ) : (
            <>
              Marks Available: <span className='text-[#101828]'>{score}</span>
            </>
          )}
        </Flex>
        <Flex align={'center'} gap={'8px'} className='text-sm text-[#1D2939]'>
          <ClockIcon />
          {isEditing && editableTime ? (
            <div className='flex items-center gap-2'>
              <span>Duration:</span>
              <div className='w-28'>
                <Input
                  type='number'
                  min={1}
                  value={
                    Number.isNaN(editableTime.value) ? '' : editableTime.value
                  }
                  onChange={(e) =>
                    editableTime.onChange(Number(e.target.value))
                  }
                  className={
                    editableTime.invalid
                      ? 'border-red-500 focus:border-red-500'
                      : ''
                  }
                  aria-invalid={editableTime.invalid || undefined}
                />
                {editableTime.invalid && (
                  <p className='mt-1 text-xs text-red-500'>
                    Time must be at least 1
                  </p>
                )}
              </div>{' '}
              <span>mins</span>
            </div>
          ) : (
            <>
              Duration: <span className='text-[#101828]'>{time} mins</span>
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default QuestionItemContainer

export const ChildQuestionContainer = ({
  title,
  triggerContent,
  content,
  score,
  time,
  onClick,
  isEditing,
  editableScore,
  editableTime,
  editableChapter,
  defaultOpen = false,
  hideToggle = false,
  chapter,
}: {
  chapter: string
  title?: string
  triggerContent: ReactNode
  content: ReactNode
  score: string | number
  time: string | number
  onClick?: () => void
  isEditing?: boolean
  editableScore?: {
    value: number
    onChange: (value: number) => void
    invalid?: boolean
    canEdit?: boolean
  }
  editableTime?: {
    value: number
    onChange: (value: number) => void
    invalid?: boolean
    canEdit?: boolean
  }
  editableChapter?: {
    value: string[] | null
    chapterId?: number | null
    onChange: (chapterId: number, chapterName: string) => void
    options: { chapter_id: number; chapter_name: string }[]
    canEdit?: boolean
  }
  defaultOpen?: boolean
  hideToggle?: boolean
}) => {
  const [open, setOpen] = useState(hideToggle ? true : defaultOpen)

  const handleContainerClick = () => {
    // Allow text selection by checking if user is selecting text
    const selection = window.getSelection()
    if (selection && selection.toString().length > 0) {
      return // Don't trigger onClick if user is selecting text
    }
    onClick?.()
  }

  return (
    <Flex
      direction={'column'}
      className={clsx(
        'w-full rounded-[12px] border-[1px] px-6 py-5 transition-all duration-300 ease-in-out hover:border-[1px] hover:border-[#0069E0] hover:shadow-[0_3px_3px_#023A7B0A]',
        open ? 'bg-linear-to-b from-[#EBF4FF] from-65% to-white' : 'bg-white'
      )}
      onClick={handleContainerClick}
    >
      <Collapsible
        className='w-full'
        open={open}
        onOpenChange={() => setOpen(!open)}
      >
        <CollapsibleTrigger
          className='w-full'
          data-collapsible-trigger='true'
          onMouseDown={(e) => {
            // Allow text selection by not preventing default behavior
            const target = e.target as HTMLElement
            if (
              target.tagName === 'P' ||
              target.tagName === 'SPAN' ||
              target.tagName === 'TEXTAREA'
            ) {
              e.stopPropagation()
            }
          }}
        >
          {!hideToggle && (
            <Flex
              justify={'between'}
              align={'center'}
              className='w-full font-light text-[#475467]'
            >
              <p className='font-light text-[#475467] select-text'>{title}</p>
              {isEditing &&
              editableChapter?.canEdit &&
              editableChapter.options.length > 0 ? (
                <div
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Select
                    value={
                      editableChapter.options
                        .find((c) => c.chapter_name === editableChapter.value?.[0])
                        ?.chapter_id.toString() ?? ''
                    }
                    onValueChange={(val) => {
                      const selected = editableChapter.options.find(
                        (c) => c.chapter_id.toString() === val
                      )
                      if (selected) {
                        editableChapter.onChange(
                          selected.chapter_id,
                          selected.chapter_name
                        )
                      }
                    }}
                  >
                    <SelectTrigger className='w-fit min-w-[160px] bg-white'>
                      <SelectValue placeholder='Select chapter'>
                        <span className='text-sm text-[#475467]'>
                          {editableChapter.value?.[0] || 'Select chapter'}
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {editableChapter.options.map((opt) => (
                        <SelectItem
                          key={opt.chapter_id}
                          value={opt.chapter_id.toString()}
                        >
                          {opt.chapter_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                !!chapter && (
                  <Box
                    className={clsx(
                      'rounded-[8px] border border-[#D5D7DA] bg-white px-[10px] py-[4px]'
                    )}
                  >
                    <Text className='text-sm text-[#475467] select-text'>
                      {chapter}
                    </Text>
                  </Box>
                )
              )}
            </Flex>
          )}
          <div className='select-text'>{triggerContent}</div>
        </CollapsibleTrigger>
        {!hideToggle && (
          <div className='my-2 border-b-[1px] border-[#e6eef6]'></div>
        )}
        {/* time  */}
        <Flex justify={'end'} gap={'32px'}>
          <Flex align={'center'} gap={'8px'} className='text-sm text-[#1D2939]'>
            <StarIcon />
            {isEditing && editableScore && editableScore.canEdit ? (
              <div className='flex items-center gap-2'>
                <span>Marks Available:</span>
                <div className='w-24'>
                  <Input
                    type='number'
                    min={1}
                    value={
                      Number.isNaN(editableScore.value)
                        ? ''
                        : editableScore.value
                    }
                    onChange={(e) =>
                      editableScore.onChange(Number(e.target.value))
                    }
                    className={
                      editableScore.invalid
                        ? 'border-red-500 focus:border-red-500'
                        : ''
                    }
                    aria-invalid={editableScore.invalid || undefined}
                  />
                  {editableScore.invalid && (
                    <p className='mt-1 text-xs text-red-500'>Invalid score</p>
                  )}
                </div>
              </div>
            ) : (
              <>
                Marks Available: <span className='text-[#101828]'>{score}</span>
              </>
            )}
          </Flex>
          <Flex align={'center'} gap={'8px'} className='text-sm text-[#1D2939]'>
            <ClockIcon />
            {isEditing && editableTime && editableTime.canEdit ? (
              <div className='flex items-center gap-2'>
                <span>Duration:</span>
                <div className='w-28'>
                  <Input
                    type='number'
                    min={1}
                    value={
                      Number.isNaN(editableTime.value) ? '' : editableTime.value
                    }
                    onChange={(e) =>
                      editableTime.onChange(Number(e.target.value))
                    }
                    className={
                      editableTime.invalid
                        ? 'border-red-500 focus:border-red-500'
                        : ''
                    }
                    aria-invalid={editableTime.invalid || undefined}
                  />
                  {editableTime.invalid && (
                    <p className='mt-1 text-xs text-red-500'>
                      Time must be at least 1
                    </p>
                  )}
                </div>{' '}
                <span>mins</span>
              </div>
            ) : (
              <>
                Duration: <span className='text-[#101828]'>{time} mins</span>
              </>
            )}
          </Flex>
        </Flex>
        <CollapsibleContent className='mt-2'>{content}</CollapsibleContent>
        <CollapsibleTrigger className='w-full' data-collapsible-trigger='true'>
          {!hideToggle && (
            <Flex
              justify={'end'}
              align={'center'}
              className='mt-4 w-full font-light text-[#475467]'
            >
              <Flex className='text-[#0057B8]' align={'center'} gap={'2'}>
                {open ? 'See less' : 'See more'}
                <ChevronDown
                  className={clsx(
                    open ? 'rotate-180' : '',
                    'transition-all duration-300 ease-in-out'
                  )}
                />
              </Flex>
            </Flex>
          )}
        </CollapsibleTrigger>
      </Collapsible>
    </Flex>
  )
}
